// Engineering numeric-suffix normalization for the Required Heat Duty input.
//
// The CPQ "Flame Cell Sizing" script (operations.json op 347) converts an "MM"
// suffix to a raw number by multiplying by 1e6 — e.g. "3MM" → 3000000 — via
//   /^[\d.]+\s*MM$/i  then  Number(...) * 1e6
// but it does so ONLY when the Generate Housing Options (CALCULATE2) button is
// pressed, and ONLY for the double-M form. Operators routinely type a single
// "M" for "million" (e.g. "3M" → 3,000,000), so this helper accepts both M and
// MM and applies the same ×1e6 factor, normalizing on field entry instead of
// waiting for the sizing button.
//
// Heat duty is expressed in Btu/hr where these values are in the millions, so
// "M" unambiguously means million here. We deliberately do NOT invent a
// "K"/×1000 rule — no such suffix exists anywhere in the CPQ export.
// THOUSANDS SEPARATORS MUST NOT SURVIVE. Required Heat Duty is the only free-text
// field any hide rule compares numerically (the Main Assembly Size options gate on
// `__["REQUIRED_HEAT_DUTY/REQUIRED_HEAT_DUTY"] <= 125000` and friends), and a comma
// makes that coercion NaN — every threshold test then fails and the size list comes
// up EMPTY. Typing "3,500,000" used to do exactly that: this function stripped the
// commas to test for the M/MM suffix but returned `raw` when there was none, so the
// commas went into the answer state untouched. It also keeps the value out of the
// ID_KEY fingerprint's way, which hashes this field as a raw string ("3,500,000" and
// "3500000" would otherwise fingerprint as two different configurations).
export function normalizeHeatDutySuffix(raw: string): string {
  // Strip commas AND whitespace, so "3,500,000", "3 500 000" (the international
  // spelling) and a stray "3,5 00,000" typo all clean up. Only values that then read
  // as a plain number are rewritten, so a non-numeric entry containing spaces is
  // still returned untouched.
  const s = String(raw ?? "").replace(/[,\s]/g, "");
  // Accept "3M", "3MM", "3 MM", "3.5mm" (case-insensitive, whitespace already gone).
  const m = s.match(/^([\d.]+)(MM|M)$/i);
  if (m) {
    const n = Number(m[1]);
    return isFinite(n) ? String(n * 1e6) : raw;
  }
  // No suffix: still commit the CLEANED value when what remains is a plain number,
  // so separators and stray whitespace can never reach a rule. Anything that isn't
  // numeric is left exactly as typed (an operator mid-edit, or a real typo they
  // should see) rather than silently rewritten.
  if (/^(?:\d+|\d*\.\d+)$/.test(s)) return s;
  return raw;
}

// Questions whose free-text value goes through normalizeHeatDutySuffix, declared by
// the running configurator (src/products/). Both consumers -- the input widget and
// the config-load path -- go through these accessors so they cannot drift: a field
// normalized on typing but not on load would still come back comma'd from a
// configuration saved before the fix.
import { getProfile } from "../products";

/** Does this configurator normalize this field's typed value? */
export function isSuffixNormalized(product: string, code: string): boolean {
  return getProfile(product).suffixNormalizedFields.has(code);
}

/** Every field this configurator normalizes, for the load path to sweep. */
export function suffixNormalizedFields(product: string): ReadonlySet<string> {
  return getProfile(product).suffixNormalizedFields;
}
